import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { FormMode } from '../../../core/constants';
import { ListItem } from '../../../../../../../libs/Shared/components/model/listItem';
import { User } from '../../../models/users/user';
import { UserService } from '../../../models/users/userService';
import { UIErrorHandler } from '../../../core/uiErrorHandler';
import { EditUserInfoDialogComponent } from '../edit-user/edit-user-dialog/edit-user-info-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CacheService } from '../../../core/storage/cacheService';
import { IconType } from '../../../../../../../libs/Shared/components/icon/icon';
import { Textbox, TextModes } from '../../../../../../../libs/Shared/components/inputs/textbox/textbox';
import { Selectbox } from '../../../../../../../libs/Shared/components/inputs/selectbox/selectbox';
import { UserRole } from '../../../../../../../libs/Shared/components/model/UserRoles';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  standalone: false,
})
export class UserFormComponent implements OnChanges {
  @Input() user: Partial<User> | undefined;
  @Input() enableEditing: boolean = false; // shows the Edit button
  @Input() showCloseButton: boolean = false; // had to hack this in so that you couldn't click edit again while editing to open another dialog
  @Input() enableSaving: boolean = false; // shows the save/cancel/submit buttons
  @Input() merchantId: string | undefined;
  @Input() enableSelectExistingUser: boolean = false;
  @Input() enableRoleSelection: boolean = true;
  @Input() showRequired: boolean = false;
  @Input() isNew: boolean = true;
  @Input() initialMode: FormMode = FormMode.View; // "view"; // view | edit  -- defines the initial state of the form
  @Output() saved = new EventEmitter<any>(); // Partial<User>
  @Output() created = new EventEmitter<string>();
  @Output() cancelled = new EventEmitter();
  @Output() close = new EventEmitter<void>();
  isValid: boolean = false;
  

  @ViewChildren(Textbox) validatedTextBoxes:
    | QueryList<Textbox>
    | undefined;
  @ViewChildren(Selectbox) validatedDropDowns:
    | QueryList<Selectbox>
    | undefined;

  _currentMode: FormMode = this.initialMode;
  TextModes = TextModes;
  IconType = IconType;

  userFromSearch: Partial<User> | undefined;
  userFormData: Partial<User> = User.createEmpty(); // copied from passed-in user object
  selectedRole: UserRole = '';
  roleOptions: ListItem[] = [];
  userSelectionMode = '';

  linkExisting: boolean = false;
  emailAccepted: boolean = false;
  lastEmailValue: string = '';
  emailSearchIsDirty = false; // true if email address to search changes while a search is executing
  emailSearchInProgress: boolean = false;
  emailValidationMessage: string = '';

  public get formMode(): typeof FormMode {
    return FormMode;
  }

  public getData(): Partial<User> | undefined {
    if (this.userFromSearch) {
      return this.userFromSearch;
    } else {
      return this.userFormData;
    }
  }

  constructor(
    private userService: UserService,
    private uiErrorHandler: UIErrorHandler,
    public editUserDialog: MatDialog,
    private router: Router,
  ) {
    // TODO: Build this dynamically
    this.roleOptions = CacheService.roleList;
  }

  ngOnInit() {
    if (this.initialMode == FormMode.New) {
      this._currentMode = FormMode.Edit;
      this.enableEditing = true;
    } else {
      this._currentMode = this.initialMode;
    }
  }

  ngAfterViewInit(): void {
    this.checkValidity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user']) {
      this._currentMode = FormMode.View;
    }

    if (this.user != null && this.user != undefined && this.user.id != '') {
      this.userFormData = User.clone(this.user);
      // this.emailAccepted = true;
      // this.lastEmailValue = this.userFormData.emailAddress + "";

      this.selectedRole = this.user.originalRole || '';
    }
  }

  checkValidity(): boolean { // probably not necessary to return a value, but just in case
    const textBoxesValid = !this.validatedTextBoxes ? false : this.validatedTextBoxes.toArray().map(control => control.checkValiditySilently()).every(isValid => isValid)
    if (!this.isNew) {
      this.isValid = textBoxesValid
      return this.isValid
    }
    
    const dropDownsValid = !this.validatedDropDowns ? false : this.validatedDropDowns.toArray().map(control => control.checkValiditySilently()).every(isValid => isValid)
    this.isValid = textBoxesValid && dropDownsValid;
    return this.isValid;
  }

  public save(): Promise<any> {
    if (!this.selectedRole && !this.userFormData.originalRole) {
      throw 'no role selected';
    }

    if (this.linkExisting) {
      if (!this.merchantId) {
        throw 'no merchant selected';
      }

      return this.userService
        .addUserToMerchant(
          this.merchantId,
          this.userFormData,
          this.selectedRole,
        )
        .then((user) => {
          return this.userFormData;
        })
        .catch((message) => {
          throw message;
        });
    } else {
      //if (!this.emailAccepted) {
      //  throw "The email address is not valid or is already in use.";
      //}

      let errors = this.uiErrorHandler.gatherFormErrors(
        true,
        this.validatedTextBoxes?.toArray(),
        this.validatedDropDowns?.toArray(),
      );

      if (errors.length > 0) {
        throw errors[0].text;
      }

      try {
        this._validateForm();
      } catch (e) {
        throw e;
      }

      if (this.user?.id) {
        return this.userService
          .saveUserInfo(this.userFormData)
          .then(() => {
            return this.userFormData;
          })
          .catch((message) => {
            //this.uiErrorHandler.handleError(message);
            throw message;
          });
      } else if (this.merchantId) {
        return this.userService
          .addMerchantUser(
            this.merchantId,
            this.userFormData,
            this.selectedRole,
          )
          .then((user) => {
            return user;
          })
          .catch((message) => {
            // this.uiErrorHandler.handleError(message);
            throw message;
          });
      } else {
        return this.userService
          .createUser(this.userFormData, this.selectedRole)
          .then((user) => {
            return user;
          })
          .catch((message) => {
            // this.uiErrorHandler.handleError(message);
            throw message;
          });
      }
    }
  }

  private _validateForm() {
    // throw error or void

    this._validateFirstName();

    this._validateLastName();

    this._validateEmail();

    this._validateRole();
  }

  private _validateLastName() {
    if (
      !this.userFormData.lastName ||
      this.userFormData.lastName.trim() == ''
    ) {
      throw 'last name cannot be blank';
    }
  }

  private _validateFirstName() {
    if (
      !this.userFormData.firstName ||
      this.userFormData.firstName.trim() == ''
    ) {
      throw 'first name cannot be blank';
    }
  }

  private _validateRole() {
    // for new users only
    if (
      this.enableRoleSelection &&
      (!this.userFormData.id || this.userFormData.id == '')
    ) {
      if (this.selectedRole == '') {
        //if (this.userFormData.originalRole == "") {
        throw 'you must select a role';
      }
    }
  }

  private _validateEmail() {
    if (
      !this.userFormData.emailAddress ||
      this.userFormData.emailAddress.trim() == ''
    ) {
      throw 'email address cannot be blank';
    }
  }

  resetView() {
    //   this._currentMode = this.initialMode;
    //   if (this.user) {
    //     this.userFormData = this.user;
    //   }
    //   else {
    //     this.userFormData = User.createEmpty();
    //     this.selectedRole = "";
    //     // if (this.validatedDropDowns) {
    //     //   let ddls = this.validatedDropDowns?.toArray()
    //     //   for (var i=0; i<ddls.length; i++) {
    //     //     ddls[i].clearSelection();
    //     //   }
    //     // }
    //   }
  }

  btnEditInfo_click() {
    let ref = this.editUserDialog.open(EditUserInfoDialogComponent, {
      disableClose: true,
      data: {
        user: this.user,
        merchantId: this.merchantId,
      },
    });

    this.router.events.subscribe(() => {
      ref.close();
    });

    ref.afterClosed().subscribe(({ user }) => {
      if (user) {
        this.user = User.clone(user)
        this.userFormData = User.clone(user);
        //this.saved.emit(user);
      }
    });
  }

  btnCancelEditInfo_click() {
    this.resetView();

    if (!this.user) {
      // If there's no existing user, then we are in "new user" mode, so this
      // should act the same as the main cancel button
      this.cancelled.emit();
    }
  }
}
